import { useAtomValue } from 'jotai';

import {
  flagOverridesAtom,
  getOverridesAtPageLoad,
  overridesEqual,
} from '@/features/flags-dev/overrides';
import {
  Banner,
  BannerContent,
  BannerDescription,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { Info } from 'lucide-react';

export const MissingFlagsmithEnvKeyBanner = () => {
  if (process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY) return null;
  return (
    <Banner subVariant="warning" className="mb-3">
      <BannerContent>
        <BannerTitle>Flagsmith overrides won&apos;t apply</BannerTitle>
        <BannerDescription className="break-words [&_code]:break-all [&_code]:font-mono">
          <code>NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY</code> is not set, so the Flagsmith
          provider was never initialized. Toggling Flagsmith rows here will have no
          effect. Add the key to <code>.env.local</code> and restart the dev server.
        </BannerDescription>
      </BannerContent>
    </Banner>
  );
};

export const MissingPostHogEnvKeyBanner = () => {
  if (process.env.NEXT_PUBLIC_POSTHOG_KEY) return null;
  return (
    <Banner subVariant="warning" className="mb-3">
      <BannerContent>
        <BannerTitle>PostHog provider values unavailable</BannerTitle>
        <BannerDescription className="break-words [&_code]:break-all [&_code]:font-mono">
          <code>NEXT_PUBLIC_POSTHOG_KEY</code> is not set, so PostHog has no real flags
          loaded. Overrides still apply, but provider values will be blank and only flags
          discovered at runtime will appear. Add the key to <code>.env.local</code> and
          restart to see the full list.
        </BannerDescription>
      </BannerContent>
    </Banner>
  );
};

export const ForceEnableAllFlagsBanner = () => {
  if (process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS !== 'true') {
    return null;
  }
  return (
    <Banner subVariant="warning" className="mb-3">
      <BannerContent>
        <BannerTitle>Overrides will be ignored</BannerTitle>
        <BannerDescription className="break-words [&_code]:break-all [&_code]:font-mono">
          <code>NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS=true</code> is set, which
          patches <code>posthog.isFeatureEnabled</code> to always return <code>true</code>
          . Unset it in <code>.env.local</code> and restart the dev server to use this
          helper.
        </BannerDescription>
      </BannerContent>
    </Banner>
  );
};

/** Most flags are read once at boot, so an override needs a reload to take effect. */
export const ReloadRequiredBanner = () => {
  const current = useAtomValue(flagOverridesAtom);
  const initial = getOverridesAtPageLoad();
  if (overridesEqual(current, initial)) return null;
  return (
    <Banner subVariant="subtle" className="mt-3">
      <BannerContent icon={<Info />}>
        <BannerTitle>Reload to apply changes</BannerTitle>
      </BannerContent>
      <Button
        type="button"
        size="sm"
        onClick={() => window.location.reload()}
        data-testid="dev-tools-feature-flags-reload"
      >
        Reload
      </Button>
    </Banner>
  );
};
